import { Button } from '@/General/Button'
import { Modal } from '@/Modal/Modal'
import { stacks, GAME_MODES, DISPLAY_MODES } from './stack-config'
import { TempSettings } from './types'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  tempSettings: TempSettings
  onTempSettingsChange: (settings: TempSettings) => void
  onSave: () => void
}

export const SettingsModal = ({
  isOpen,
  onClose,
  tempSettings,
  onTempSettingsChange,
  onSave
}: SettingsModalProps) => {
  const updateTempSetting = (key: keyof TempSettings, value: any) => {
    onTempSettingsChange({ ...tempSettings, [key]: value })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Settings" maxWidth="lg">
      <div className="space-y-4">
        {/* Mode Setting */}
        <div>
          <label htmlFor="mode-select" className="block text-sm font-medium text-gray-700">
            Mode
          </label>
          <select
            id="mode-select"
            value={tempSettings.tempMode}
            onChange={e => updateTempSetting('tempMode', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
          >
            <option value={GAME_MODES.RANDOM}>Random</option>
            <option value={GAME_MODES.GUESS_POSITION}>Guess Position</option>
            <option value={GAME_MODES.GUESS_CARD}>Guess Card</option>
          </select>
        </div>

        {/* Stack Range Settings */}
        <div>
          <label htmlFor="stack-start" className="block text-sm font-medium text-gray-700">
            Stack Start
          </label>
          <input
            id="stack-start"
            type="number"
            min="1"
            max="52"
            value={tempSettings.tempStackStart}
            onChange={e => updateTempSetting('tempStackStart', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
        
        <div>
          <label htmlFor="stack-end" className="block text-sm font-medium text-gray-700">
            Stack End
          </label>
          <input
            id="stack-end"
            type="number"
            min="1"
            max="52"
            value={tempSettings.tempStackEnd}
            onChange={e => updateTempSetting('tempStackEnd', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>

        {/* Timer Setting */}
        <div>
          <label htmlFor="timer-seconds" className="block text-sm font-medium text-gray-700">
            Timer (seconds)
          </label>
          <input
            id="timer-seconds"
            type="number"
            min="0"
            value={tempSettings.tempTimerSeconds}
            onChange={e => updateTempSetting('tempTimerSeconds', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
          />
          <p className="text-xs text-gray-500">
            Timer must be 0 (disabled) or at least 3 seconds.
          </p>
        </div>

        {/* Stack Selection */}
        <div>
          <label htmlFor="stack-name" className="block text-sm font-medium text-gray-700">
            Stack Name
          </label>
          <select
            id="stack-name"
            value={tempSettings.tempSelectedStackName}
            onChange={e => updateTempSetting('tempSelectedStackName', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
          >
            {stacks.map((s, idx) => (
              <option key={idx} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Display Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Display Mode</label>
          <div className="mt-1 flex items-center">
            <input
              type="radio"
              id="multiple"
              name="displayMode"
              value={DISPLAY_MODES.MULTIPLE}
              checked={tempSettings.tempDisplayMode === DISPLAY_MODES.MULTIPLE}
              onChange={() => updateTempSetting('tempDisplayMode', DISPLAY_MODES.MULTIPLE)}
              className="h-4 w-4 text-blue-600 border-gray-300"
            />
            <label htmlFor="multiple" className="ml-2 mr-4 text-sm text-gray-700">
              Multiple
            </label>
            <input
              type="radio"
              id="auto"
              name="displayMode"
              value={DISPLAY_MODES.AUTO}
              checked={tempSettings.tempDisplayMode === DISPLAY_MODES.AUTO}
              onChange={() => {
                const timerValue = typeof tempSettings.tempTimerSeconds === 'string'
                  ? parseInt(tempSettings.tempTimerSeconds, 10)
                  : tempSettings.tempTimerSeconds
                onTempSettingsChange({
                  ...tempSettings,
                  tempDisplayMode: DISPLAY_MODES.AUTO,
                  tempTimerSeconds: timerValue === 0 ? '3' : tempSettings.tempTimerSeconds,
                })
              }}
              className="h-4 w-4 text-blue-600 border-gray-300"
            />
            <label htmlFor="auto" className="ml-2 text-sm text-gray-700">
              Auto
            </label>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse mt-4 -m-4">
        <Button onClick={onSave} color="blue">
          Save
        </Button>
        <Button onClick={onClose} color="gray" className="sm:mt-0 ml-3">
          Cancel
        </Button>
      </div>
    </Modal>
  )
}